import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { syncEngine } from "../local/sync";
import { queueRideReview } from "../local/reviewRequests";

// #3088 - the scribe's REVIEW button: "something weird happened, or I messed
// something up". Confirms with an optional comment, then queues the ride for
// the scoreadmin review queue through the offline outbox
// (local/reviewRequests.js) and hands control back via onSent, which the
// caller uses to return the scribe to the ride list. Never waits on the
// network, so it behaves identically offline.
export default function ReviewRideButton({ rideId, isTempRide = false, onSent, disabled = false }) {
  const { t } = useTranslation("score");
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  const close = () => {
    if (sending) return;
    setOpen(false);
    setError(false);
  };

  const confirm = async () => {
    setSending(true);
    setError(false);
    try {
      await queueRideReview({ rideId, isTempRide, comment });
    } catch {
      setSending(false);
      setError(true);
      return;
    }
    // Fire-and-forget: scribe-fe's axios has no timeout, so awaiting a flush
    // here could hang the scribe on a flaky venue connection. The engine's
    // own tick drains the op; this only nudges it and refreshes the badge.
    if (syncEngine.isOnline()) void syncEngine.flush();
    else void syncEngine.refreshPending();
    setSending(false);
    setOpen(false);
    setComment("");
    onSent?.();
  };

  return (
    <>
      <Button
        variant="outlined"
        color="warning"
        disabled={disabled}
        onClick={() => setOpen(true)}
        data-testid="review-ride-button"
      >
        {t("scoring.reviewButton")}
      </Button>
      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle>{t("scoring.reviewDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t("scoring.reviewDialogBody")}</DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label={t("scoring.reviewCommentLabel")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            slotProps={{
              htmlInput: { maxLength: 1000, "data-testid": "review-comment-input" },
            }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="review-error">
              {t("scoring.reviewError")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={sending} onClick={close} data-testid="review-cancel-button">
            {t("scoring.reviewCancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            loading={sending}
            onClick={confirm}
            data-testid="review-confirm-button"
          >
            {t("scoring.reviewConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
